// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import { imagePath } from 'presentations/assets';
import {
  downloadsPath,
  featureListPath,
  helpCenterPath,
  pricingPath,
  resourceCategoryPath,
  aboutPath,
  newsListPath,
  contactPath,
  customerStoryListPath,
  changelogListPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';

// Logo

export const getLogo = () => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    logo: imagePath('structured_data/logo_symbol.png'),
    url: 'https://www.jicoo.com/',
  });
};

// Breadcrumbs

interface Breadcrumb {
  title: string;
  link?: string;
}
interface BreadcrumbsProps {
  items: Breadcrumb[];
}
export const getBreadcrumbs = (props: BreadcrumbsProps) => {
  const origin = process.env['JICOO_HOST'];
  const home = {
    '@type': 'ListItem',
    position: 0,
    name: translate('siteName'),
    item: origin,
  };
  const items = props.items.map((item, position) => {
    const o = {
      '@type': 'ListItem',
      position: position + 1,
      name: item.title,
      item: '',
    };
    if (item.link) o.item = `${origin}${item.link}`;
    return o;
  });
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [home, ...items],
  });
};

// Frequently Asked Questions

interface FrequentlyAskedQuestion {
  question: string;
  answer: string;
}
interface FrequentlyAskedQuestionsProps {
  items: FrequentlyAskedQuestion[];
}
export const getFrequentlyAskedQuestions = (
  props: FrequentlyAskedQuestionsProps,
) => {
  const items = props.items.map((item, position) => {
    return {
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    };
  });
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [items],
  });
};

export const getHomeSiteNavigationElement = () => {
  const router = useRouter();
  const locale = router.locale;
  const t = translateInComponent(translations);
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'SiteNavigationElement',
    hasPart: [
      {
        '@type': 'CollectionPage',
        name: t('Feature'),
        url: featureListPath(),
      },
      {
        '@type': 'AboutPage',
        name: t('Downloads'),
        url: downloadsPath(),
      },
      {
        '@type': 'AboutPage',
        name: t('Pricing'),
        url: pricingPath(),
      },
      {
        '@type': 'AboutPage',
        name: t('Contact'),
        url: contactPath(),
      },
      {
        '@type': 'AboutPage',
        name: t('About'),
        url: aboutPath(),
      },
      {
        '@type': 'CollectionPage',
        name: t('Customer Stories'),
        url: customerStoryListPath(),
      },
      {
        '@type': 'CollectionPage',
        name: t('Changelog'),
        url: changelogListPath(),
      },
      {
        '@type': 'CollectionPage',
        name: t('News'),
        url: newsListPath(),
      },
      {
        '@type': 'CollectionPage',
        name: t('Help'),
        url: helpCenterPath(router.locale),
      },
    ],
  });
};

export const getWebPage = (props: {
  url: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}) => {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': props.url,
    },
    headline: props.title,
    datePublished: props.createdAt,
    dateModified: props.updatedAt,
    author: {
      '@type': 'Organization',
      name: translate('corpSiteName'),
    },
    publisher: {
      '@type': 'Organization',
      name: translate('corpSiteName'),
      address: {
        '@type': 'PostalAddress',
        addressLocality: translate('address text.2'),
        addressRegion: 'JP',
        postalCode: translate('address text.0'),
        streetAddress: translate('address text.1'),
      },
      logo: {
        '@type': 'ImageObject',
        url: imagePath('logo/www.svg'),
        width: 130,
        height: 34,
      },
    },
  });
};
